import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  FormBuilderModule,
  INavClickPayload,
  NohsFormControlValue,
  NohsFormSubmissionPayload,
  NohsPresentationDTO,
} from '@nohs/form-builder';
import { BehaviorSubject, exhaustMap, Subject, Subscription, switchMap, take } from 'rxjs';
import { CONTEXT_IDS, STORAGE_KEYS } from '../../app.constants';
import { FormsApiService, getFlowInstanceOptions } from '../../providers/formsapi.service';
import { LoadingService } from '../../providers/loading.service';
import { PostMessageService } from '../../providers/postMessage.service';
import { StorageService } from '../../providers/storage.service';

interface HttpRequestParams {
  masterFlowId: number;
  payload: NohsFormSubmissionPayload;
  activityId: number | undefined;
  navAction?: string | undefined;
}

@Component({
  selector: 'app-flow',
  templateUrl: './flow.component.html',
  styleUrls: ['./flow.component.scss'],
  imports: [CommonModule, FormBuilderModule],
})
export class FlowComponent implements OnInit, OnDestroy {
  private formApi = inject(FormsApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private cdRef = inject(ChangeDetectorRef);
  private postMessageService = inject(PostMessageService);
  private storage = inject(StorageService);

  masterFlowId!: number;
  activityId!: number;
  presentation$: BehaviorSubject<NohsPresentationDTO | null> = new BehaviorSubject<NohsPresentationDTO | null>(null);
  subscription: Subscription = new Subscription();
  displayBlockingSpinner = false;
  displayNonblockingSpinner = false;
  postInstanceId = false;
  submitSubject: Subject<HttpRequestParams> = new Subject<HttpRequestParams>();

  constructor() {
    if (this.storage.getItem(STORAGE_KEYS.CONTEXT_ID) === CONTEXT_IDS.CAP) {
      this.postInstanceId = true;
    }
    // set user local timezone
    if (
      Intl &&
      Intl.DateTimeFormat() &&
      Intl.DateTimeFormat().resolvedOptions() &&
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ) {
      this.storage.setItem(STORAGE_KEYS.DFE_USER_TIME_ZONE, Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
    this.route.queryParamMap
      .pipe(
        switchMap((params) => {
          const flowId = parseInt(params.get('flowId') || '0');
          const activityId = parseInt(params.get('activityId') || '0');
          // this.postInstanceId = parseInt(params.get('postInstanceId') || '0') === 1;
          this.masterFlowId = flowId;
          this.activityId = activityId;

          const options = this.buildGetFlowInstanceOptions(params);

          return this.formApi.getFlowInstance2(options);
        }),
        take(1)
      )
      .subscribe((value) => {
        this.presentation$.next(value);
        if (this.postInstanceId) {
          this.postMessageService.postInstanceId(value.instanceId);
        }
      });

    this.subscription.add(
      this.presentation$.subscribe((value) => {
        if (value !== null && value !== undefined) {
          if (value.status === 3 && value.presentations.length == 1 && value.presentations[0].formSpec.formId === 0) {
            this.router.navigate(['/callback', 'complete']);
          }
        }
      })
    );

    this.subscription.add(
      this.submitSubject
        .pipe(
          exhaustMap((params: HttpRequestParams) => {
            if (!params.navAction) {
              return this.formApi.submitForm(params.masterFlowId, params.payload, params.activityId);
            } else {
              return this.formApi.navClick(params.masterFlowId, params.navAction, params.payload, params.activityId);
            }
          })
        )
        .subscribe((response) => {
          this.presentation$.next({ ...response });
        })
    );
  }

  private buildGetFlowInstanceOptions(params: ParamMap) {
    const options: getFlowInstanceOptions = { flowId: parseInt(params.get('flowId') || '0') };

    const activityId = parseInt(params.get('activityId') || '0');
    if (activityId > 0) {
      options.activityId = activityId;
    }

    const enterpriseId = params.get('enterpriseId');
    if (enterpriseId) {
      options.enterpriseId = enterpriseId;
    }

    const editInstanceId = params.get('editInstanceId');
    if (editInstanceId) {
      options.editInstanceId = editInstanceId;
    }

    const resultIds = params.get('resultIds');
    if (resultIds) {
      options.resultIds = resultIds;
    }

    const noRehydrate = params.get('noRehydrate');
    if (noRehydrate) {
      options.noRehydrate = noRehydrate === 'true';
    }

    const eventOwnerId = parseInt(params.get('eventOwnerId') || '0');
    if (eventOwnerId > 0) {
      options.eventOwnerId = eventOwnerId;
    }

    const eventOwnerTypeId = parseInt(params.get('eventOwnerTypeId') || '0');
    if (eventOwnerTypeId > 0) {
      options.eventOwnerTypeId = eventOwnerTypeId;
    }

    const sourceId = parseInt(params.get('sourceId') || '0');
    if (sourceId > 0) {
      options.sourceId = sourceId;
    }

    return options;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.loadingService.blockingCount$.subscribe((value) => {
        this.displayBlockingSpinner = value >= 1;
        this.cdRef.detectChanges();
      })
    );

    this.subscription.add(
      this.loadingService.nonBlockingCount$.subscribe((value) => {
        this.displayNonblockingSpinner = value >= 1;
        this.cdRef.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  navClick(payload: INavClickPayload): void {
    let cancelOrCompleteHasValues = false;
    switch (payload.type) {
      case 'cancelOrComplete':
        if (payload.data && payload.data.variables && Array.isArray(payload.data.variables)) {
          payload.data.variables.forEach((d: NohsFormControlValue) => {
            if (d.value !== '') {
              cancelOrCompleteHasValues = true;
            }
          });
        }
        if (cancelOrCompleteHasValues) {
          payload.data.variables.push({ name: 'signalComplete', value: 1 });
          this.submitSubject.next({
            masterFlowId: this.masterFlowId,
            payload: payload.data,
            activityId: this.activityId > 0 ? this.activityId : undefined,
          });
        } else {
          this._cancel();
        }
        break;
      case 'submit':
        this.submitSubject.next({
          masterFlowId: this.masterFlowId,
          payload: payload.data,
          activityId: this.activityId > 0 ? this.activityId : undefined,
        });
        break;
      case 'cancel':
        this._cancel();
        break;
      default:
        this.submitSubject.next({
          masterFlowId: this.masterFlowId,
          payload: payload.data,
          activityId: this.activityId > 0 ? this.activityId : undefined,
          navAction: payload.type,
        });
        break;
    }
  }

  private _cancel() {
    this.router.navigate(['/callback', 'cancel']);
  }
}
